import {
  AttentionPoint,
  AttentionPointContext,
  BoatmenService,
  Clearance,
  ClearanceStatus,
  DeclarationType,
  EmergencyProcedureSummary,
  PilotFromDistanceRequestStatus,
  TaskMessageStatus,
  TidalWindow,
  VisitSummaryStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import lodash from "lodash";
import {
  EmergencySummaryExpanded,
  isAccessPoint,
  isBerth,
  MovementPlace
} from "./emergency-overview-item/emergency-overview-item.component";
import {ClearanceService} from "../../visit-details/visit/clearance/clearance-service";
import {DeclarationMessageStatus} from "../visit-overview/visit-overview-item/visit-overview.utils";
import moment from "moment";

export class EmergencyOverviewUtils {

  static getTaskStatuses = (summary: EmergencySummaryExpanded, declarationTypes: DeclarationType[], clearanceService: ClearanceService) =>
    lodash.flatMap(declarationTypes, d => this.declarationTypeToStatus(summary, d, clearanceService)) || []

  static toExtendedSummary = (summary: EmergencySummaryWithDummyfields): EmergencySummaryExpanded => {
    const movementTargetPlace = this.getMovementTargetPlace(summary);
    const movementOriginPlace = this.getMovementOriginPlace(summary, movementTargetPlace);

    return {...summary, movementTargetPlace, movementOriginPlace} as EmergencySummaryExpanded;
  }

  // The vessel's next destination: either it's leaving the port (exit point), or it's heading to a specific berth.
  private static getMovementTargetPlace = (summary: EmergencySummaryWithDummyfields): MovementPlace =>
    summary.movementTarget.exitPoint
      ? summary.exitPoint
      : summary.berthVisitSummaries.find(bv => bv.id === summary.movementTarget.berthVisitId) as MovementPlace;

  // Where the vessel is coming from, given where it's headed next.
  private static getMovementOriginPlace = (summary: EmergencySummaryWithDummyfields, movementTargetPlace: MovementPlace): MovementPlace => {
    // Leaving the port: the vessel departs from its last visited berth, or straight from the entry point if it never berthed.
    if (summary.movementTarget.exitPoint) {
      return (lodash.last(summary.berthVisitSummaries) as MovementPlace) ?? summary.entryPoint;
    }

    // Heading to a berth: find the berth the vessel is coming from.
    const targetIndex = summary.berthVisitSummaries.findIndex(bv => bv.id === movementTargetPlace.id);
    const targetBerth = summary.berthVisitSummaries[targetIndex];
    const hasArrivedAtTarget = !!targetBerth?.ata;
    const previousBerth = summary.berthVisitSummaries[targetIndex - 1] as MovementPlace;

    // Already at the target berth: it's both the origin and the destination (e.g. waiting to depart).
    if (hasArrivedAtTarget) {
      return movementTargetPlace;
    }
    // Still approaching the target berth from a previous berth.
    if (previousBerth) {
      return previousBerth;
    }
    // No previous berth: coming straight from the entry point.
    return summary.entryPoint;
  }

  // The relevant timestamp for the origin place: when the vessel departed if it has already left,
  // or when it arrived if it's still there (waiting to depart towards the target place).
  public static getMovementOriginTime = (summary: EmergencySummaryExpanded): string => {
    const originPlace = summary.movementOriginPlace;
    if (!isBerth(originPlace)) {
      return summary.etaPbp;
    }

    const hasLeftOriginPlace = originPlace.id !== summary.movementTargetPlace.id;
    return hasLeftOriginPlace ? originPlace.atd : originPlace.ata;
  }

  // The relevant timestamp for the target place: when the vessel arrived if it's already there,
  // otherwise the expected arrival time.
  public static getMovementTargetTime = (summary: EmergencySummaryExpanded): string => {
    const targetPlace = summary.movementTargetPlace;
    const originPlace = summary.movementOriginPlace;
    if (!isBerth(targetPlace)) {
      return summary.etdPort;
    }
    if (originPlace.id == targetPlace.id) {
      return targetPlace.etd;
    }
    return targetPlace.ata ?? targetPlace.eta;
  }

  // The vessel's current draught as reported by the agent for the target berth.
  public static getAgentReportedVesselDraught = (summary: EmergencySummaryExpanded): number | null => {
    return isBerth(summary.movementOriginPlace) ? summary.movementOriginPlace.vesselDraught ?? null : summary.incomingMovementVesselDraught;
  }

  // The vessel's actual draught as reported by the harbour master for the movement underway.
  // Incoming trips don't have a target berth movement yet, so the port-level incoming movement info is used instead.
  public static getActualVesselDraught = (summary: EmergencySummaryExpanded): number | null => {
    const originPlace = summary.movementOriginPlace;
    const harbourMasterInfo = isAccessPoint(originPlace) ? summary.incomingMovementHarbourMasterInfo
      : originPlace.harbourMasterInfo;
    return harbourMasterInfo?.vesselDraught ?? null;
  }

  // The tidal window confirmed by the harbour master for the movement underway.
  // Incoming trips don't have a target berth movement yet, so the port-level incoming movement info is used instead.
  public static getTidalWindow = (summary: EmergencySummaryExpanded): TidalWindow | null => {
    const originPlace = summary.movementOriginPlace;
    const harbourMasterInfo = isAccessPoint(originPlace) ? summary.incomingMovementHarbourMasterInfo
      : originPlace.harbourMasterInfo;
    return harbourMasterInfo?.tidalWindowStatus?.tidalWindows?.[0] ?? null;
  }

  // Whether the vessel is tide restricted for the movement underway, as flagged by the harbour master.
  public static isTidalShip = (summary: EmergencySummaryExpanded): boolean => {
    const originPlace = summary.movementOriginPlace;
    const harbourMasterInfo = isAccessPoint(originPlace) ? summary.incomingMovementHarbourMasterInfo
      : originPlace.harbourMasterInfo;
    return harbourMasterInfo?.tidalWindowStatus?.tidalShip ?? false;
  }

  // The shore-based pilotage (pilot LOA) status confirmed by the harbour master for the movement underway.
  // Incoming trips don't have a target berth movement yet, so the port-level incoming movement info is used instead.
  public static getPilotFromDistanceStatus = (summary: EmergencySummaryExpanded): PilotFromDistanceRequestStatus | null => {
    const originPlace = summary.movementOriginPlace;
    const harbourMasterInfo = isAccessPoint(originPlace) ? summary.incomingMovementHarbourMasterInfo
      : originPlace.harbourMasterInfo;
    return harbourMasterInfo?.pilotFromDistance ?? null;
  }

  // Whether shore-based pilotage (pilot LOA) applies for the movement underway, as flagged by the agent proxying the captain.
  public static isShoreBasedPilotage = (summary: EmergencySummaryExpanded): boolean => {
    const originPlace = summary.movementOriginPlace;
    return isAccessPoint(originPlace) ? summary.incomingMovementRemotePilotage : summary.outgoingMovementRemotePilotage;
  }

  public static getBoatmenService(summary: EmergencySummaryExpanded): BoatmenService | null {
    let boatmenService: BoatmenService | null = null;
    if (isBerth(summary.movementTargetPlace) && summary.movementTargetPlace.boatmenAtArrival?.required) {
      boatmenService = summary.movementTargetPlace.boatmenAtArrival;
    } else if (isBerth(summary.movementOriginPlace) && summary.movementOriginPlace.boatmenAtDeparture?.required) {
      boatmenService = summary.movementOriginPlace.boatmenAtDeparture;
    }

    return boatmenService;
  }

  private static declarationTypeToStatus = (summary: EmergencySummaryExpanded, declarationType: DeclarationType, clearanceService: ClearanceService): DeclarationMessageStatus[] => {
    switch (declarationType) {
      case DeclarationType.VISIT:
        return this.clearanceDeclarationTypeToStatus(clearanceService, summary, declarationType, AttentionPointContext.ekh);
      case DeclarationType.SECURITY:
        return this.clearanceDeclarationTypeToStatus(clearanceService, summary, declarationType, AttentionPointContext.sspi);
      case DeclarationType.HEALTH:
        return this.clearanceDeclarationTypeToStatus(clearanceService, summary, declarationType, AttentionPointContext.mdoh);
      case DeclarationType.WASTE:
        return this.clearanceDeclarationTypeToStatus(clearanceService, summary, declarationType, AttentionPointContext.emas);
      case DeclarationType.DANGEROUS_GOODS:
        return this.clearanceDeclarationTypeToStatus(clearanceService, summary, declarationType, AttentionPointContext.emgs);
      default:
        return [this.defaultMapper(summary, declarationType)];
    }
  }

  private static clearanceDeclarationTypeToStatus(clearanceService: ClearanceService, summary: EmergencySummaryExpanded, declarationType: DeclarationType, clearanceKey: AttentionPointContext): DeclarationMessageStatus[] {
    let clearance: Clearance;
    // For visit the clearance is defined based on the movement between berths, for others over the whole visit
    if (declarationType === DeclarationType.VISIT) {
      clearance = summary.movementForEkhClearance
        ? clearanceService.getClearance(summary.clearances, clearanceKey, summary.movementForEkhClearance?.to, summary.movementForEkhClearance?.from)
        : null;
    } else {
      clearance = clearanceService.getClearance(summary.clearances, clearanceKey);
    }
    const taskStatus = summary.taskStatuses[declarationType]?.status;
    if (declarationType === DeclarationType.VISIT) {
      const orderOverdueDeadline = EmergencyOverviewUtils.getDeadlineInfo(summary, 'ORDER_OVERDUE');
      if (orderOverdueDeadline) {
        return [{
          name: this.declarationTypeToName(declarationType),
          taskStatus: TaskMessageStatus.DEADLINE_MISSED,
          messages: ['Not ordered within the required time window']
        }];
      }
      else if (clearance == null && ![TaskMessageStatus.ACCEPTED, TaskMessageStatus.DELIVERED].includes(taskStatus)) {
        return [this.defaultMapper(summary, declarationType)];
      } else if (clearance === null && taskStatus === TaskMessageStatus.DELIVERED) {
        return [{
          name: this.declarationTypeToName(declarationType),
          taskStatus: TaskMessageStatus.ACCEPTED,
          messages: this.getMessagesForDeclarationType(summary, declarationType)
        }]
      }
    }
    return [this.defaultClearanceMapper(clearance, summary, clearanceService, declarationType, clearanceKey)];
  }

  private static defaultClearanceMapper(clearance: Clearance, summary: EmergencySummaryExpanded, clearanceService: ClearanceService, declarationType: DeclarationType, clearanceKey: AttentionPointContext) {
    if (clearance) {
      const taskStatus = this.clearanceStatusToTaskStatus(clearance.status);
      const declarationStatus = summary.taskStatuses[declarationType]?.status || TaskMessageStatus.UNKNOWN;
      return {
        name: this.declarationTypeToName(declarationType),
        taskStatus: taskStatus === TaskMessageStatus.PENDING && [TaskMessageStatus.DELIVERED, TaskMessageStatus.UNKNOWN].includes(declarationStatus)
          ? declarationStatus : taskStatus,
        customLabel: taskStatus === TaskMessageStatus.WARNING ? 'Actions required' : null,
        messages: clearance.remarks
          ? [clearance.remarks].concat(clearance.attentionPoints.map(a => this.translateAttentionPoint(clearanceService, clearanceKey, a)))
          : clearance.attentionPoints.map(a => this.translateAttentionPoint(clearanceService, clearanceKey, a))
      };
    } else {
      return this.defaultMapper(summary, declarationType);
    }
  }

  private static translateAttentionPoint(clearanceService: ClearanceService, clearanceKey: AttentionPointContext, attentionPoint: AttentionPoint): string {
    return `${clearanceService.translate(clearanceKey, attentionPoint.type)}${attentionPoint.remarks ? ': ' + attentionPoint.remarks : ''}`;
  }

  private static defaultMapper(summary: EmergencySummaryExpanded, declarationType: DeclarationType): DeclarationMessageStatus {
    const deadline = this.getDeadlineInfo(summary, declarationType.toString());
    if (deadline) {
      return {
        name: this.declarationTypeToName(declarationType),
        taskStatus: deadline.messageStatus,
        messages: this.getMessagesForDeclarationType(summary, declarationType),
        customLabel: deadline.deadlineLabel
      };
    }
    return {
      name: this.declarationTypeToName(declarationType),
      taskStatus: summary.taskStatuses[declarationType]?.status || TaskMessageStatus.UNKNOWN,
      messages: this.getMessagesForDeclarationType(summary, declarationType),
    };
  }

  static getDeadlineInfo(summary: EmergencySummaryExpanded, deadlineKey: string): ({
    messageStatus: TaskMessageStatus,
    deadlineLabel: string
  }) {
    if ([VisitSummaryStatus.DEPARTED, VisitSummaryStatus.CANCELLED].includes(summary.visitStatus) && deadlineKey !== "ORDER_OVERDUE") {
      return null;
    }
    const orderDeadline = summary.deadlines[deadlineKey];
    const status = orderDeadline?.end && moment().isAfter(moment(orderDeadline.end))
      ? TaskMessageStatus.DEADLINE_MISSED : orderDeadline?.start && moment().isAfter(moment(orderDeadline.start)) ? TaskMessageStatus.DEADLINE_CLOSE : null;
    if (status) {
      return {
        messageStatus: status,
        deadlineLabel: this.getDeadlineLabel(status, orderDeadline?.end)
      }
    }
  }

  static getDeadlineLabel(status: TaskMessageStatus, deadline: string): string {
    const formatCountdown = (countdownInMinutes: number): string => {
      const hours = Math.floor(countdownInMinutes / 60);
      const minutes = countdownInMinutes - hours * 60;
      return `${hours > 0 ? `${hours} hours` : `${minutes} minutes`}`;
    }
    if (!deadline) {
      return null;
    }
    const missed = status === TaskMessageStatus.DEADLINE_MISSED;
    const endTime = moment(deadline);
    const diffInMinutes = endTime.diff(moment(), "minutes");
    return missed ? `Deadline missed` : `Deadline in ${formatCountdown(diffInMinutes)}`;
  }

  private static getMessagesForDeclarationType = (summary: EmergencySummaryExpanded, declarationType: DeclarationType): string[] => {
    const taskStatus = summary.taskStatuses[declarationType];
    const status: TaskMessageStatus = taskStatus?.status || TaskMessageStatus.UNKNOWN;
    switch (declarationType) {
      case DeclarationType.VISIT:
        return summary.unreadHarbourRemarks && summary.harbourMasterRemarks ? [summary.harbourMasterRemarks] : [];
      default:
        return status === TaskMessageStatus.REJECTED ? taskStatus?.rejectReasons : [];
    }
  }

  private static declarationTypeToName = (declarationType: DeclarationType): string => {
    switch (declarationType) {
      // Port authority
      case DeclarationType.VISIT:
        return "Port visit";
      case DeclarationType.HEALTH:
        return "Health";
      case DeclarationType.SECURITY:
        return "Security";
      case DeclarationType.WASTE:
        return "Waste";
      case DeclarationType.DANGEROUS_GOODS:
        return "Dangerous goods";
      // case DeclarationType.HARBOUR_MASTER:
      //   return "Harbour master";
      // case DeclarationType.LOODSWEZEN:
      //   return "Loodswezen";
   }
    return declarationType;
  }

  private static clearanceStatusToTaskStatus(clearanceStatus: ClearanceStatus): TaskMessageStatus {
    switch (clearanceStatus) {
      case ClearanceStatus.UNKNOWN:
        return TaskMessageStatus.DISABLED;
      case ClearanceStatus.PENDING:
        return TaskMessageStatus.PENDING;
      case ClearanceStatus.APPROVED_WITH_REMARKS:
        return TaskMessageStatus.WARNING;
      case ClearanceStatus.APPROVED:
        return TaskMessageStatus.ACCEPTED;
      case ClearanceStatus.REJECTED:
        return TaskMessageStatus.REJECTED;
    }
  }
}

export interface EmergencySummaryWithDummyfields extends EmergencyProcedureSummary {
  harbourMasterStatus?: "ACCEPTED";
  loodswezenStatus?: "ACCEPTED";
  operations?: boolean;
  orientation?: "BB";
  loodswezenAanwezig: boolean;
  roeiersAanwezig: boolean;
  helicopterbeloodsing: boolean;
  loaApproved: boolean;
  pinned: boolean;
}
